// components/PublicResults.js - Fixed WebSocket Synchronization
import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { CheckCircle, XCircle, Minus, Trophy, Clock, RefreshCw, Play, Pause, AlertTriangle, CheckCircle2, Timer, Flame, Wifi, WifiOff } from 'lucide-react';
import { getCompetitionById, getClimbByCompetition, getTimerState } from '../services/apiServices';
import { slugToId } from '../util/slug';
import socketService from '../services/socketService';

export const PublicResults = () => {
  const { shareableId } = useParams();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [competition, setCompetition] = useState(null);
  const [climbData, setClimbData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [timerState, setTimerState] = useState(null);
  const [localRemainingTime, setLocalRemainingTime] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [currentRound, setCurrentRound] = useState(1);
  const [errorMessage, setErrorMessage] = useState(null);

  // Refs
  const isMountedRef = useRef(true);
  const timerIntervalRef = useRef(null);
  const lastUpdateTimeRef = useRef(Date.now());

  // Compute remaining time from end time
  const computeRemainingFromEndTime = (timer) => {
    if (!timer?.currentEndTime) return 0;

    const now = Date.now();
    const end = new Date(timer.currentEndTime).getTime();

    const diffInMs = end - now;
    const diffInSeconds = Math.max(0, Math.ceil(diffInMs / 1000));

    return diffInSeconds;
  };

  // Stop local timer
  const stopLocalTimer = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
      console.log('⏹️ Local countdown stopped');
    }
  };

  // Handle timer updates - memoized
  const handleTimerUpdate = useCallback((data) => {
    if (!isMountedRef.current || !data) return;

    console.log('📊 Timer update received:', {
      status: data.status,
      currentPhase: data.currentPhase,
      remainingTime: data.remainingTime,
      currentRound: data.currentRound,
      currentEndTime: data.currentEndTime,
      timestamp: new Date().toISOString()
    });

    // Update last sync time
    lastUpdateTimeRef.current = Date.now();

    // Update timer state
    setTimerState(data);

    // Update current round
    if (data.currentRound) {
      setCurrentRound(data.currentRound);
    }

    // Calculate remaining time from end time for more accuracy
    const computedRemaining = computeRemainingFromEndTime(data);
    if (computedRemaining > 0) {
      setLocalRemainingTime(computedRemaining);
    } else if (data.remainingTime && data.remainingTime > 0) {
      // Fallback to server's remaining time
      setLocalRemainingTime(data.remainingTime);
    }

    // Manage local countdown based on timer state
    manageLocalTimer(data);
  }, []);

  // Manage local timer countdown
  const manageLocalTimer = useCallback((timerData) => {
    if (!timerData) {
      stopLocalTimer();
      return;
    }

    const { status, currentPhase } = timerData;

    // Stop existing timer
    stopLocalTimer();

    // Only run local countdown for active phases
    // Continue local timer even if status is not in the list, as long as we have a currentEndTime
    if (timerData.currentEndTime &&
      (status === 'running' ||
        ['countdown', 'climbing', 'wrap_time', 'warning'].includes(currentPhase))) {
      console.log('▶️ Starting local countdown for:', status || currentPhase, 'with currentEndTime');
      startLocalTimer();
    } else {
      console.log('⏸️ Not starting local countdown:', { status, currentPhase, hasEndTime: !!timerData.currentEndTime });
    }
  }, []);

  // Start local countdown timer
  const startLocalTimer = useCallback(() => {
    stopLocalTimer();

    timerIntervalRef.current = setInterval(() => {
      setLocalRemainingTime(prevTime => {
        // Calculate remaining time from currentEndTime if available
        if (timerState?.currentEndTime) {
          const computedRemaining = computeRemainingFromEndTime(timerState);
          if (computedRemaining >= 0) {
            // When countdown reaches zero, request fresh state
            if (computedRemaining === 0 && prevTime > 0) {
              console.log('⏰ Local countdown reached zero - requesting fresh state');
              const competitionId = slugToId(shareableId);
              if (competitionId && socketService.getConnectionStatus()) {
                socketService.getTimerState(competitionId);
              }
            }
            return computedRemaining;
          }
        }

        // Fallback: decrement by 1 second
        const newTime = Math.max(0, prevTime - 1);

        // When countdown reaches zero, request fresh state
        if (newTime === 0 && prevTime > 0) {
          console.log('⏰ Local countdown reached zero - requesting fresh state');
          const competitionId = slugToId(shareableId);
          if (competitionId && socketService.getConnectionStatus()) {
            socketService.getTimerState(competitionId);
          }
        }

        return newTime;
      });
    }, 1000);
  }, [shareableId, timerState]);

  // Fetch competition data
  // const fetchCompetition = useCallback(async () => {
  //   try {
  //     const competitionId = slugToId(shareableId);
  //     const response = await getCompetitionById(competitionId);
  //     if (response && response !== "false") {
  //       const competitionData = await response.json();
  //       setCompetition(competitionData);

  //       // Fetch initial timer state via HTTP
  //       await fetchInitialTimerState(competitionId);
  //     }
  //   } catch (error) {
  //     console.error("Error fetching competition:", error);
  //     setErrorMessage('Failed to load competition data');
  //   }
  // }, [shareableId]);

  // Fetch initial timer state via HTTP API
  const fetchInitialTimerState = async (competitionId) => {
    try {
      console.log('📡 Fetching initial timer state via HTTP...');
      const response = await getTimerState(competitionId);
      if (response && response !== "false") {
        const data = await response.json();
        if (data.success && data.data) {
          console.log('✅ Initial timer state loaded:', data.data);
          handleTimerUpdate(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching initial timer state:', error);
    }
  };

  // Fetch climb data
  const fetchClimbData = useCallback(async () => {
    try {
      setIsLoading(true);
      const competitionId = slugToId(shareableId);
      const response = await getClimbByCompetition(competitionId);
      if (response && response !== "false") {
        const climbDataResult = await response.json();
        console.log('📊 Fetched climb data:', climbDataResult);
        await fetchInitialTimerState(competitionId);
        setClimbData(climbDataResult);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error("Error fetching climb data:", error);
      setErrorMessage('Failed to load climb data');
    } finally {
      setIsLoading(false);
    }
  }, [shareableId]);

  // Initial data fetch
  useEffect(() => {
    if (shareableId) {
      fetchClimbData();
    }
  }, [shareableId, fetchClimbData]);

  // Add this useEffect for periodic competition data refresh
  useEffect(() => {
    if (!shareableId) return;

    const fetchCompetitionPeriodically = async () => {
      try {
        const competitionId = slugToId(shareableId);
        const response = await getCompetitionById(competitionId);
        if (response && response !== "false") {
          const competitionData = await response.json();
          setCompetition(prev => {
            // Only update if data actually changed
            if (JSON.stringify(prev) !== JSON.stringify(competitionData)) {
              console.log('🔄 Competition data updated:', {
                finalannocement: competitionData.finalannocement,
                enddate: competitionData.enddate
              });
              return competitionData;
            }
            return prev;
          });
        }
      } catch (error) {
        console.error("Error fetching competition periodically:", error);
      }
    };

    // Initial fetch
    fetchCompetitionPeriodically();

    // Set up interval to fetch every 3 seconds
    const interval = setInterval(fetchCompetitionPeriodically, 3000);

    return () => clearInterval(interval);
  }, [shareableId]);
  // Initialize WebSocket connection
  useEffect(() => {
    isMountedRef.current = true;
    let reconnectTimeout;
    let stateCheckInterval;

    const setupWebSocketConnection = async () => {
      if (!competition?._id) {
        console.log('Waiting for competition ID...');
        return;
      }

      try {
        console.log('🔄 Setting up AWS WebSocket for competition:', competition._id);
        setConnectionStatus('connecting');
        setErrorMessage(null);

        // Connect to WebSocket
        await socketService.connect();

        if (!isMountedRef.current) return;

        console.log('✅ WebSocket connected, setting up listeners...');
        setIsSocketConnected(true);
        setConnectionStatus('connected');

        // Join competition room
        const userInfo = {
          userId: 'results-viewer-' + Math.random().toString(36).substr(2, 9),
          userType: 'public'
        };
        socketService.joinCompetition(competition._id, userInfo);
        console.log('🎯 Joined competition room:', competition._id);

        // Request initial timer state via WebSocket
        socketService.getTimerState(competition._id);

        // Set up periodic state check every 2 seconds to stay in sync
        stateCheckInterval = setInterval(() => {
          if (socketService.getConnectionStatus() && competition._id) {
            console.log('🔄 Periodic state check via WebSocket');
            socketService.getTimerState(competition._id);
          }
        }, 2000);

        // Set up event listeners
        socketService.onTimerUpdate((data) => {
          if (isMountedRef.current && data) {
            handleTimerUpdate(data);
          }
        });

        socketService.onTimerError((error) => {
          if (isMountedRef.current) {
            console.error('❌ Timer error:', error);
            setErrorMessage(error.message || 'Timer error occurred');
          }
        });

      } catch (error) {
        console.error('❌ WebSocket setup error:', error);
        if (isMountedRef.current) {
          setIsSocketConnected(false);
          setConnectionStatus('error');
          setErrorMessage('Connection lost - Updates via polling');

          // Attempt reconnect after delay
          reconnectTimeout = setTimeout(() => {
            if (isMountedRef.current && competition?._id) {
              console.log('🔄 Retrying WebSocket connection...');
              setupWebSocketConnection();
            }
          }, 5000);
        }
      }
    };

    setupWebSocketConnection();

    return () => {
      isMountedRef.current = false;

      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }

      if (stateCheckInterval) {
        clearInterval(stateCheckInterval);
      }

      // Cleanup WebSocket listeners
      if (socketService) {
        socketService.offTimerUpdate();
        socketService.offTimerError();

        // Leave competition room
        if (competition?._id) {
          socketService.leaveCompetition(competition._id);
        }
      }

      stopLocalTimer();
      console.log('🧹 Cleaned up WebSocket connection');
    };
  }, [competition?._id, handleTimerUpdate, shareableId]);

  // HTTP polling fallback when WebSocket is disconnected
  useEffect(() => {
    if (!competition?._id) return;

    let pollingInterval;

    const startPolling = () => {
      console.log('🔄 Starting HTTP polling for timer state');
      pollingInterval = setInterval(async () => {
        // Poll regardless of WebSocket status to ensure timer stays in sync
        // But poll more frequently when WebSocket is disconnected
        const pollInterval = isSocketConnected ? 10000 : 3000; // 10s when connected, 3s when disconnected

        console.log('🔄 Polling timer state via HTTP...');
        try {
          const response = await getTimerState(competition._id);
          if (response && response !== "false") {
            const data = await response.json();
            if (data.success && data.data) {
              handleTimerUpdate(data.data);
            }
          }
        } catch (error) {
          console.error('Polling error:', error);
        }
      }, isSocketConnected ? 5000 : 2000);
    };

    startPolling();

    // Restart polling with new interval when socket connection changes
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [competition?._id, isSocketConnected, handleTimerUpdate]);

  // Periodic climb data refresh
  useEffect(() => {
    if (!competition?._id) return;

    const refreshInterval = setInterval(() => {
      console.log('🔄 Periodic climb data refresh...');
      fetchClimbData();
    }, 10000); // Refresh every 10 seconds

    return () => clearInterval(refreshInterval);
  }, [competition?._id, fetchClimbData]);

  // Refresh climb data more frequently during wrap_time to get latest results
  useEffect(() => {
    if (!timerState || timerState.currentPhase !== 'wrap_time') return;

    const fastRefreshInterval = setInterval(() => {
      console.log('🔄 Fast refresh during wrap time...');
      fetchClimbData();
    }, 3000); // Refresh every 5 seconds during wrap time

    return () => clearInterval(fastRefreshInterval);
  }, [timerState?.currentPhase, fetchClimbData]);

  // Current time update
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Calculate score
  const calculateScore = useCallback((event) => {
    if (!event) return 0;

    let zonePoints = 0;
    let topPoints = 0;
    const Zone = competition?.zonepoints || 10;
    const Top = competition?.toppoints || 25;
    const penalty = competition?.penalty || 0.1;

    if (event.zoneReached && event.zoneAttempt) {
      zonePoints = Zone - (event.zoneAttempt - 1) * penalty;
    }

    if (event.topReached && event.topAttempt) {
      topPoints = Top - (event.topAttempt - 1) * penalty;
    }

    return Math.max(zonePoints, topPoints);
  }, [competition?.zonepoints, competition?.toppoints, competition?.penalty]);

  // Format climb result display
  const formatClimbResult = useCallback((score) => {
    if (!score) return null;

    if (score.topReached) {
      if (score.zoneReached) {
        return `${score.zoneAttempt}-Z, ${score.topAttempt}-T`;
      }
      return `${score.topAttempt}-T`;
    } else if (score.zoneReached) {
      return `${score.zoneAttempt}-Z`;
    } else if (score.attempts > 0) {
      return `${score.attempts}-A`;
    }
    return null;
  }, []);

  // Format time
  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Timer display info
  const getTimerDisplayInfo = () => {
    if (!timerState || !timerState.status) {
      return {
        color: 'bg-slate-500',
        display: 'READY',
        icon: Clock,
        timerColor: 'text-slate-700'
      };
    }

    const { status, currentPhase } = timerState;
    const warningTime = competition?.warningTime || 60;

    // Handle completed states when time reaches 0
    if (localRemainingTime === 0) {
      if (status === 'countdown') {
        return {
          color: 'bg-blue-600',
          display: 'COUNTDOWN COMPLETE',
          icon: CheckCircle2,
          timerColor: 'text-blue-700'
        };
      }
      if (status === 'climbing') {
        return {
          color: 'bg-emerald-600',
          display: 'CLIMBING COMPLETE',
          icon: CheckCircle2,
          timerColor: 'text-emerald-700'
        };
      }
      if (status === 'wrap_time') {
        return {
          color: 'bg-purple-600',
          display: 'WRAP COMPLETE',
          icon: CheckCircle2,
          timerColor: 'text-purple-700'
        };
      }
    }

    // Handle active phases
    const phase = currentPhase || status;

    switch (phase) {
      case 'countdown':
        return {
          color: 'bg-blue-600',
          display: 'COUNTDOWN',
          icon: Timer,
          timerColor: 'text-blue-700'
        };
      case 'climbing':
        if (localRemainingTime <= warningTime) {
          return {
            color: 'bg-orange-600 animate-pulse',
            display: 'WARNING',
            icon: Flame,
            timerColor: 'text-orange-700 animate-pulse'
          };
        }
        return {
          color: 'bg-emerald-600',
          display: 'CLIMBING',
          icon: Play,
          timerColor: 'text-emerald-700'
        };
      case 'wrap_time':
        return {
          color: 'bg-purple-600',
          display: `WRAP TIME - ROUND ${currentRound}`,
          icon: AlertTriangle,
          timerColor: 'text-purple-700'
        };
      case 'warning':
        return {
          color: 'bg-orange-600 animate-pulse',
          display: 'WARNING',
          icon: AlertTriangle,
          timerColor: 'text-orange-700 animate-pulse'
        };
      case 'paused':
        return {
          color: 'bg-yellow-600',
          display: 'PAUSED',
          icon: Pause,
          timerColor: 'text-yellow-700'
        };
      case 'completed':
        return {
          color: 'bg-slate-600',
          display: 'COMPLETED',
          icon: Trophy,
          timerColor: 'text-slate-700'
        };
      case 'idle':
        return {
          color: 'bg-slate-500',
          display: 'READY',
          icon: Clock,
          timerColor: 'text-slate-700'
        };
      default:
        return {
          color: 'bg-slate-500',
          display: 'READY',
          icon: Clock,
          timerColor: 'text-slate-700'
        };
    }
  };

  const timerInfo = getTimerDisplayInfo();
  const TimerIcon = timerInfo.icon;

  // Connection status display
  const getConnectionBadge = () => {
    switch (connectionStatus) {
      case 'connected':
        return {
          bg: 'bg-emerald-600',
          text: 'LIVE',
          icon: Wifi,
          description: 'Real-time updates'
        };
      case 'connecting':
        return {
          bg: 'bg-yellow-600 animate-pulse',
          text: 'CONNECTING',
          icon: RefreshCw,
          description: 'Establishing connection...'
        };
      case 'disconnected':
      case 'error':
        return {
          bg: 'bg-orange-600',
          text: 'SYNCING',
          icon: RefreshCw,
          description: 'Auto-updating every 10s'
        };
      default:
        return {
          bg: 'bg-slate-600',
          text: 'UNKNOWN',
          icon: WifiOff,
          description: 'Connection status unknown'
        };
    }
  };

  const connectionBadge = getConnectionBadge();
  const ConnectionIcon = connectionBadge.icon;

  // Data management functions
  const getAllClimbers = useCallback(() => {
    if (!competition || !competition.competitors) return [];

    const competitionClimbers = competition.competitors.map(climberId => {
      const climbInfo = climbData?.length > 0 ? climbData.find(item => item.climberID?._id === climberId) : {};
      if (climbInfo && climbInfo?.climberID) return climbInfo.climberID;
      return { _id: climberId, name: `Climber ${climberId.substring(0, 6)}`, bib: '?' };
    }).filter(Boolean);

    const climbDataClimbers = [];
    if (climbData?.length > 0)
      climbData.forEach(item => {
        if (item.climberID && item.climberID._id && !competitionClimbers.find(c => c._id === item.climberID._id)) {
          climbDataClimbers.push(item.climberID);
        }
      });

    return [...competitionClimbers, ...climbDataClimbers];
  }, [competition, climbData]);

  const getBoulders = useCallback(() => {
    if (!competition || !competition.boulders) return [];
    return competition.boulders.map(boulder => ({ id: boulder._id, name: boulder.name }));
  }, [competition]);

  const getScoreForClimberBoulder = useCallback((climberId, boulderId) => {
    return climbData?.length > 0 ? climbData.find(item => item.climberID?._id === climberId && item.boulderID === boulderId) : null;
  }, [climbData]);

  const getTotalScoreForClimber = useCallback((climberId) => {
    const boulders = getBoulders();
    return boulders.reduce((total, boulder) => {
      const score = getScoreForClimberBoulder(climberId, boulder.id);
      return total + calculateScore(score);
    }, 0);
  }, [getBoulders, getScoreForClimberBoulder, calculateScore]);

  const getClimberCompletion = useCallback((climberId) => {
    const boulders = getBoulders();
    const completed = boulders.filter(boulder => {
      const score = getScoreForClimberBoulder(climberId, boulder.id);
      return score && (score.topReached || score.zoneReached || score.attempts > 0);
    }).length;
    return boulders.length > 0 ? Math.round((completed / boulders.length) * 100) : 0;
  }, [getBoulders, getScoreForClimberBoulder]);

  const getSortedClimbers = useCallback(() => {
    const climbers = getAllClimbers();
    return climbers.sort((a, b) => {
      const scoreDiff = getTotalScoreForClimber(b._id) - getTotalScoreForClimber(a._id);
      if (scoreDiff !== 0) return scoreDiff;

      const completionDiff = getClimberCompletion(b._id) - getClimberCompletion(a._id);
      if (completionDiff !== 0) return completionDiff;

      return (a.name || '').localeCompare(b.name || '');
    });
  }, [getAllClimbers, getTotalScoreForClimber, getClimberCompletion]);

  const calculateSummary = useCallback(() => {
    const totalScores = climbData?.length;
    const tops = climbData.length > 0 ? climbData?.filter(item => item.topReached).length : 0;
    const zones = climbData.length > 0 ? climbData?.filter(item => item.zoneReached).length : 0;
    const uniqueClimbers = getAllClimbers().length;
    const uniqueBoulders = getBoulders().length;
    const completedClimbers = getAllClimbers().filter(climber => getClimberCompletion(climber._id) === 100).length;

    return { totalScores, tops, zones, totalClimbers: uniqueClimbers, totalBoulders: uniqueBoulders, completedClimbers };
  }, [climbData, getAllClimbers, getBoulders, getClimberCompletion]);

  const getCompletionColor = (percentage) => {
    if (percentage === 100) return 'bg-emerald-600';
    if (percentage >= 75) return 'bg-green-500';
    if (percentage >= 50) return 'bg-yellow-500';
    if (percentage >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };



  // Calculate total remaining time from competition end date
  const getTotalRemainingTime = () => {
    if (!timerState?.enddate) return 0;

    const now = Date.now();
    const end = new Date(timerState.enddate).getTime();

    const diffInMs = end - now;
    return Math.max(0, Math.ceil(diffInMs / 1000));
  };

  const formatTotalTime = (seconds) => {
    if (seconds <= 0) return '00:00:00';

    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!competition) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <h1 className="text-3xl text-slate-800 mb-2 font-bold">Loading Results...</h1>
          <p className="text-slate-600">Please wait while we load the competition results.</p>
        </div>
      </div>
    );
  }

  const climbers = getSortedClimbers();
  const boulders = getBoulders();
  const summary = calculateSummary();
  const totalRemainingTime = getTotalRemainingTime();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 md:p-6">
      {/* Error Message */}
      {errorMessage && (
        <div className="fixed top-4 left-4 right-4 z-50 max-w-7xl mx-auto">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-red-500 mr-3" />
                <p className="text-red-700 font-medium">{errorMessage}</p>
              </div>
              <button
                onClick={() => setErrorMessage(null)}
                className="text-red-500 hover:text-red-700"
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto space-y-6 pt-8">

        {/* Header - Split Layout */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">

          {/* Left section badges */}
          <div className="flex items-center gap-3 flex-wrap">
            <Badge className="text-sm px-4 py-1.5 bg-indigo-600 text-white font-semibold shadow-md">
              {competition.category}
            </Badge>

            <Badge className={`text-sm px-4 py-1.5 ${timerInfo.color} text-white font-semibold shadow-md`}>
              {timerInfo.display}
            </Badge>

            <Badge className={`text-sm px-4 py-1.5 text-white font-semibold ${connectionBadge.bg}`}>
              <ConnectionIcon className="size-3 mr-1" />
              {connectionBadge.text}
            </Badge>

            <span className="text-sm text-slate-600">{connectionBadge.description}</span>
          </div>

          {/* RIGHT: Announcement Badge */}
          {competition?.finalannocement === 1 && (
            <Badge className="text-sm px-4 py-1.5 bg-yellow-600 text-white font-semibold shadow-md">
              Provisional Announcement
            </Badge>
          )}
          {competition?.finalannocement === 2 && (
            <Badge className="text-sm px-4 py-1.5 bg-green-600 text-white font-semibold shadow-md">
              Final Announcement
            </Badge>
          )}
        </div>


        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-indigo-300 to-transparent"></div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="text-center bg-white/50 rounded-lg p-3 border border-emerald-200">
            <p className="text-xs text-slate-600 font-semibold mb-1">Competitors</p>
            <p className="text-3xl text-slate-800 font-black">{summary.totalClimbers}</p>
          </div>

          <div className="text-center bg-green-50 rounded-lg p-3 border border-green-200">
            <p className="text-xs text-green-700 font-semibold mb-1">Total Tops</p>
            <p className="text-3xl text-green-700 font-black">{summary.tops}</p>
          </div>

          <div className="text-center bg-yellow-50 rounded-lg p-3 border border-yellow-200">
            <p className="text-xs text-yellow-700 font-semibold mb-1">Total Zones</p>
            <p className="text-3xl text-yellow-700 font-black">{summary.zones}</p>
          </div>

          <div className="text-center bg-white/50 rounded-lg p-3 border border-emerald-200">
            <p className="text-xs text-emerald-700 font-semibold mb-1">Current Time</p>
            <p className="text-3xl text-emerald-700 font-black">{formatTime(localRemainingTime)}</p>
          </div>

          <div className="text-center bg-white/50 rounded-lg p-3 border border-emerald-200">
            <p className="text-xs text-emerald-700 font-semibold mb-1"> {timerState?.status === 'completed' ? 'Completed At' : 'Total Time'}</p>
            <p className="text-3xl text-emerald-700 font-black">   {timerState?.status === 'completed'
              ? (() => {
                const endDate = competition?.enddate || timerState?.enddate;
                if (endDate) {
                  const date = new Date(endDate);
                  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                }
                return '--:--';
              })()
              : formatTotalTime(totalRemainingTime)
            }</p>
          </div>
        </div>

        {/* Results Table */}
        <Card className="bg-white/60 backdrop-blur-md border-white/40 shadow-lg overflow-hidden">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-slate-800 flex items-center gap-2">
                Live Results
                <div className="flex items-center gap-2 text-sm font-normal">
                  <div className={`w-2 h-2 rounded-full ${connectionStatus === 'connected' ? 'bg-emerald-500 animate-pulse' :
                    connectionStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' :
                      'bg-red-500'
                    }`}></div>
                  <span className="text-slate-600">{connectionBadge.text}</span>
                </div>
              </CardTitle>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <RefreshCw className={`size-4 ${isLoading ? 'animate-spin' : ''}`} />
                  <span>Updated: {lastUpdate ? lastUpdate.toLocaleTimeString() : 'Never'}</span>
                </div>
                <button
                  onClick={fetchClimbData}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-lg text-sm font-medium transition-colors"
                  disabled={isLoading}
                >
                  {isLoading ? 'Refreshing...' : 'Refresh Data'}
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <Table className="min-w-full">
              <TableHeader>
                <TableRow className="border-slate-300 hover:bg-slate-100/50">
                  <TableHead className="text-slate-700 font-bold w-16 p-2 text-center">Rank</TableHead>
                  <TableHead className="text-slate-700 font-bold w-16 p-2 text-center">Bib</TableHead>
                  <TableHead className="text-slate-700 font-bold min-w-[150px] p-2">Name</TableHead>
                  {/* <TableHead className="text-slate-700 font-bold min-w-[120px] p-2">Club/Team</TableHead> */}
                  {boulders.map(boulder => (
                    <TableHead key={boulder.id} className="text-center text-slate-700 font-bold min-w-[120px] p-2">
                      {boulder.name}
                    </TableHead>
                  ))}
                  <TableHead className="text-center text-slate-700 font-bold min-w-[100px] p-2 bg-indigo-100">
                    Total Score
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {climbers.map((climber, index) => {
                  const totalScore = getTotalScoreForClimber(climber._id);
                  const completion = getClimberCompletion(climber._id);
                  const isEvenRow = index % 2 === 0;

                  return (
                    <TableRow
                      key={climber._id}
                      className={`border-slate-200 ${isEvenRow ? 'bg-slate-50/50' : 'bg-white/40'} hover:bg-slate-100/70 transition-colors`}
                    >
                      <TableCell className="text-center bg-slate-100 text-slate-800 font-bold p-2">
                        {completion === 100 ? (
                          <div className="flex items-center justify-center gap-1">
                            <span>{index + 1}</span>
                            {/* <Trophy className="size-3 text-yellow-600" /> */}
                          </div>
                        ) : (
                          index + 1
                        )}
                      </TableCell>
                      <TableCell className="text-center bg-blue-100 text-blue-800 font-bold p-2">
                        {climber.bib || '-'}
                      </TableCell>
                      <TableCell className="text-slate-800 font-semibold p-2">{climber.name || 'Unknown Climber'}</TableCell>
                      {/* <TableCell className="text-sm text-slate-600 p-2">
                        {climber.club || climber.team || '-'}
                      </TableCell> */}

                      {boulders.map(boulder => {
                        const score = getScoreForClimberBoulder(climber._id, boulder.id);
                        const boulderScore = calculateScore(score);
                        const climbResult = formatClimbResult(score);

                        return (
                          <TableCell key={boulder.id} className={`text-center p-2 ${isEvenRow ? 'bg-slate-50/30' : 'bg-white/30'}`}>
                            {score ? (
                              <div className="space-y-1 py-1">
                                <div className="flex items-center justify-center gap-2">
                                  <span className="text-sm font-semibold text-slate-800">
                                    {score.attempts > 0 ? (
                                      <span>{climbResult}: <span className='text-green-600'>{boulderScore.toFixed(1)}</span></span>
                                    ) : <span>--</span>}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className="py-1">
                                <Minus className="size-4 text-slate-300 mx-auto" />
                              </div>
                            )}
                          </TableCell>
                        );
                      })}

                      <TableCell className="text-center bg-indigo-100 font-black text-lg text-indigo-900 p-2">
                        {totalScore.toFixed(1)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Legend */}
        <Card className="bg-white/60 backdrop-blur-md border-white/40 shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-slate-800 text-sm">Scoring Legend</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="size-4 text-green-600" />
                <span className="text-slate-700">T# = Top (attempt #)</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="size-4 text-yellow-600" />
                <span className="text-slate-700">Z# = Zone (attempt #)</span>
              </div>
              <div className="flex items-center gap-2">
                <XCircle className="size-4 text-slate-400" />
                <span className="text-slate-700">A# = Attempts only</span>
              </div>
              <div className="flex items-center gap-2">
                <Minus className="size-4 text-slate-300" />
                <span className="text-slate-700">No attempts</span>
              </div>
              <div className="flex items-center gap-2">
                <Trophy className="size-4 text-yellow-600" />
                <span className="text-slate-700">100% completed</span>
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <Wifi className="size-4 text-emerald-500" />
                  <span className="text-slate-700">Live updates</span>
                </div>
                <div className="flex items-center gap-2">
                  <WifiOff className="size-4 text-red-500" />
                  <span className="text-slate-700">Polling mode</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};